import { X, Star, Gift, Phone } from 'lucide-react';
import { Button } from '../ui/button';

// VIP Customer Alert Modal
export const VIPAlertModal = ({
  isOpen,
  customer,
  redeemableProducts,
  onRedeemProduct,
  onViewAllProducts,
  onClose
}) => {
  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header with celebration gradient */}
        <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 p-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-2 left-4 text-4xl animate-bounce">⭐</div>
            <div className="absolute top-4 right-6 text-3xl animate-bounce delay-100">🎉</div>
            <div className="absolute bottom-2 left-8 text-2xl animate-bounce delay-200">✨</div>
            <div className="absolute bottom-4 right-4 text-3xl animate-bounce delay-150">🌟</div>
          </div>
          <div className="relative">
            <div className="w-20 h-20 mx-auto bg-white rounded-full flex items-center justify-center shadow-lg mb-3">
              <span className="text-5xl">🐸</span>
            </div>
            <h2 className="text-2xl font-bold text-amber-900">¡CLIENTE VIP!</h2>
            <p className="text-amber-800 font-medium text-lg mt-1">{customer.name}</p>
          </div>
        </div>

        {/* Points Info */}
        <div className="p-6 bg-gradient-to-b from-amber-50 to-white">
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-amber-200 mb-4">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-4xl font-bold text-amber-600">{customer.points?.toLocaleString()}</p>
                <p className="text-sm text-gray-500">puntos</p>
              </div>
              <div className="text-3xl">=</div>
              <div className="text-center">
                <p className="text-4xl font-bold text-green-600">${customer.pesos}</p>
                <p className="text-sm text-gray-500">en canjes</p>
              </div>
            </div>
          </div>

          {/* Redeemable Products */}
          {redeemableProducts?.length > 0 && (
            <div>
              <p className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Gift className="w-5 h-5 text-amber-500" />
                Puede canjear ahora:
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {redeemableProducts.slice(0, 5).map(product => (
                  <div 
                    key={product.product_id}
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200"
                  >
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img 
                          src={product.image_url.startsWith('http') ? product.image_url : `${window.location.origin}${product.image_url}`}
                          alt="" 
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-amber-200 flex items-center justify-center">
                          <Gift className="w-5 h-5 text-amber-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-amber-600">{product.points_cost} pts</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      onClick={() => onRedeemProduct(product)}
                    >
                      Canjear
                    </Button>
                  </div>
                ))}
              </div>
              {redeemableProducts.length > 5 && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  +{redeemableProducts.length - 5} productos más disponibles
                </p>
              )}
            </div>
          )}

          {/* Suggestion for waiter */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 flex items-center gap-2">
              <span className="text-lg">💡</span>
              <span><strong>Sugerencia:</strong> Menciona al cliente que tiene puntos acumulados y puede canjear productos gratis.</span>
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Cerrar
          </Button>
          <Button
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
            onClick={onViewAllProducts}
          >
            <Gift className="w-4 h-4 mr-2" />
            Ver Todos los Canjes
          </Button>
        </div>
      </div>
    </div>
  );
};

// Loyalty Points Panel
export const LoyaltyPanel = ({
  isOpen,
  customer,
  redeemableProducts,
  onRedeemProduct,
  onSendWhatsApp,
  onClose
}) => {
  if (!isOpen || !customer?.loyalty?.can_redeem) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Star className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">¡Cliente VIP!</h2>
                <p className="text-white/90">{customer.name}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Points Display */}
          <div className="mt-4 flex items-center justify-center gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold">{customer.loyalty.points_available}</p>
              <p className="text-sm text-white/80">puntos</p>
            </div>
            <div className="text-4xl">=</div>
            <div className="text-center">
              <p className="text-3xl font-bold">${customer.loyalty.points_value_pesos}</p>
              <p className="text-sm text-white/80">pesos</p>
            </div>
          </div>
        </div>

        {/* Redeemable Products */}
        <div className="p-4">
          <p className="font-medium text-gray-700 mb-3">
            🎁 Productos que puede canjear ({redeemableProducts?.length || 0} disponibles):
          </p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(redeemableProducts || []).map(product => (
              <div 
                key={product.product_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-amber-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt="" 
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-amber-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-amber-600">{product.points_cost} puntos</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => onRedeemProduct(product)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Gift className="w-4 h-4 mr-1" />
                  Canjear
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-gray-50 border-t flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Solo Acumular
          </Button>
          <Button
            onClick={onSendWhatsApp}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white"
          >
            <Phone className="w-4 h-4 mr-2" />
            Enviar WhatsApp
          </Button>
        </div>
      </div>
    </div>
  );
};

export default { VIPAlertModal, LoyaltyPanel };
